import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Bot,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Search,
  Zap,
  Cpu,
  Brain,
  Layers,
  LineChart,
  UserCheck,
  Lock,
} from "lucide-react";

export const Route = createFileRoute("/services_/ai-marketing")({
  head: () => ({
    meta: [
      {
        title:
          "AI Marketing Services — KMWAdvertising · AI-Powered Growth & Analytics",
      },
      {
        name: "description",
        content:
          "AI Marketing Services That Combine Human Creativity with Artificial Intelligence for Smarter Business Growth. Predictive Analytics, Automation & Audience Intelligence by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "AI Marketing Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Transform Your Marketing with AI-Powered Strategies & Data-Driven Intelligence. Responsible AI automation, predictive audience modeling, and smart campaign optimization.",
      },
    ],
  }),
  component: AIMarketingServicePage,
});

export function AIMarketingServicePage() {
  const whyAIMatters = [
    {
      title: "Smarter Decision-Making",
      desc: "AI engines process massive data sets to uncover hidden customer patterns, market trends, and high-value growth opportunities.",
      icon: Brain,
    },
    {
      title: "Hyper-Personalization",
      desc: "Deliver contextually relevant recommendations, content, and messaging based on real-time user intent signals.",
      icon: Target,
    },
    {
      title: "Higher Operational Speed",
      desc: "Automate manual repetitive workflows, freeing strategy teams to focus on brand storytelling and creative positioning.",
      icon: Zap,
    },
    {
      title: "AI-Assisted Ad Optimization",
      desc: "Continuous algorithmic evaluation of bidding, audience expansion, and creative performance across paid media channels.",
      icon: Cpu,
    },
    {
      title: "Future-Ready Search Presence",
      desc: "Prepare your brand discoverability for AI Overviews, ChatGPT Search, Gemini, Perplexity, and voice assistants.",
      icon: Bot,
    },
  ];

  const aiStrategyFramework = [
    "AI Readiness & Infrastructure Assessment",
    "Marketing Process & Workflow Evaluation",
    "High-Impact AI Opportunity Identification",
    "Data Strategy & First-Party Data Cleanrooms",
    "AI Technology & Tool Stack Selection",
    "Adoption Roadmap & Team Upskilling",
    "Performance Measurement & Governance Framework",
  ];

  const audienceIntelligenceServices = [
    "Predictive Customer Cohort Segmentation",
    "Real-Time Behavioral Pattern Analysis",
    "Algorithmic Purchase Intent Scoring",
    "Customer Lifetime Value (LTV) Modeling",
    "Market Trend & Demand Forecasting",
    "Competitive Intelligence & Share-of-Voice Analysis",
  ];

  const automationCapabilities = [
    "Intelligent Lead Nurturing Workflows",
    "Automated Multi-Touch Email Marketing",
    "CRM System Synchronization (HubSpot, Salesforce)",
    "Behavior-Triggered Journey Automations",
    "Predictive Lead Scoring & Routing",
    "Automated Campaign Pacing & Reporting",
  ];

  const aiContentAndAdServices = [
    {
      title: "AI-Assisted Content Strategy",
      desc: "AI research and topic discovery paired with experienced editorial writers to ensure brand accuracy and human voice.",
      icon: FileCheck,
    },
    {
      title: "AI Advertising Optimisation",
      desc: "Algorithmic bidding and creative performance tuning across Google Ads, Meta Ads, LinkedIn Ads, and Programmatic.",
      icon: Cpu,
    },
    {
      title: "Predictive Analytics & Insights",
      desc: "Sales forecasting, attribution modeling, churn prediction, and real-time executive BI dashboards.",
      icon: LineChart,
    },
    {
      title: "Generative Engine Optimization (GEO)",
      desc: "Structuring content for visibility across conversational LLMs like ChatGPT, Perplexity, Copilot, and Gemini.",
      icon: Bot,
    },
  ];

  const responsibleAIPillars = [
    { title: "Human Review & Oversight", desc: "Every AI-generated output is audited and refined by human experts." },
    { title: "Ethical Data & Privacy", desc: "100% compliance with data privacy regulations (GDPR, DPDP) and first-party consent." },
    { title: "Brand Safety & Accuracy", desc: "Strict verification safeguards against hallucinations, bias, and off-brand tone." },
    { title: "Transparent Automation", desc: "Clear reporting on where AI algorithms automate bid decisions and audience models." },
  ];

  const kmwaStrengths = [
    "25+ years of strategic advertising & media transformation heritage",
    "Certified AI digital strategists & data engineers",
    "Practical, high-ROI AI integration (no hype, real value)",
    "Seamless hybrid of human creative storytelling + AI speed",
    "Responsible & privacy-first AI data governance",
    "Cross-channel advertising & automation capabilities",
    "Predictive attribution & executive dashboarding",
    "AI-ready SEO & Generative Engine Optimization (GEO)",
    "Enterprise-grade security and brand safety protocols",
    "Conversion-focused business growth framework",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">AI Marketing</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Bot className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                AI Marketing Services That Combine Human Creativity with Artificial Intelligence for Smarter Business Growth
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                AI-Powered Strategy, Automation & Predictive Analytics
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Transform Your Marketing with AI-Powered Strategies and Data-Driven Intelligence
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Artificial Intelligence (AI) is transforming how businesses understand customers, build campaigns and optimize marketing performance. From predictive analytics and personalized experiences to intelligent automation, modern marketing is becoming faster, smarter and more measurable.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  However, successful AI marketing is not about replacing human expertise. It is about using AI responsibly to enhance strategy, improve operational efficiency, and uncover hidden data insights that drive better executive decisions.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we help organizations integrate AI into their marketing ecosystem through strategic consulting, automation, predictive data analytics, and AI-powered paid advertising optimization.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request AI Marketing Assessment
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY AI MARKETING MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Predictive Speed & Precision
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why AI Marketing Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                AI enhances every stage of the marketing lifecycle—from audience research and automated lead nurturing to real-time ad bidding and predictive sales forecasting.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyAIMatters.map((item, idx) => {
                const AIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <AIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* STRATEGY, AUDIENCE INTELLIGENCE & AUTOMATION */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Practical Enterprise Solutions
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our AI Marketing Services
              </h2>
            </div>

            {/* STRATEGY & AUDIENCE INTELLIGENCE GRID */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* STRATEGY */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-purple)] bg-[var(--glow-purple)]/10 border border-[var(--glow-purple)]/30 mb-3">
                    <Brain className="h-3.5 w-3.5" />
                    Future-Ready Roadmap
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">AI Marketing Strategy</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Consulting & Architecture Framework
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  We help organizations identify where AI technologies create measurable commercial value while ensuring compliance with existing business goals.
                </p>

                <div className="space-y-2 pt-2">
                  {aiStrategyFramework.map((pt, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* AUDIENCE INTELLIGENCE */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-sky-400 bg-sky-500/10 border border-sky-500/30 mb-3">
                    <Users className="h-3.5 w-3.5" />
                    Predictive Audience Modeling
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">AI Audience Intelligence</h3>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Uncovering deep customer insights by analyzing multi-channel behavioral, demographic, and intent data signals.
                </p>

                <div className="space-y-2 pt-2">
                  {audienceIntelligenceServices.map((ais, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span>{ais}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CONTENT, ADVERTISING & ANALYTICS SUITES */}
            <div className="mb-16">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white">
                  AI Advertising & Predictive Analytics
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Integrating machine learning algorithms into campaign bidding, creative strategy, and sales forecasting.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {aiContentAndAdServices.map((suite, idx) => {
                  const SIcon = suite.icon;
                  return (
                    <div key={idx} className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-3">
                      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                        <SIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-display text-lg font-bold text-white">{suite.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{suite.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RESPONSIBLE AI FRAMEWORK */}
            <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white flex items-center gap-2">
                  <ShieldCheck className="h-6 w-6 text-emerald-400" />
                  Responsible & Ethical AI Marketing Framework
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  At KMWAdvertising LLP, technology supports our human experts—strategic thinking, creative authenticity, and privacy compliance remain central to every campaign.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {responsibleAIPillars.map((pil, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                    <h5 className="text-sm font-bold text-white">{pil.title}</h5>
                    <p className="text-xs text-muted-foreground leading-snug">{pil.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Media & Digital Transformation
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Bot className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              AI Marketing Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Transform Your Marketing with AI-Powered Intelligence?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our AI marketing consultants today to build a future-proof strategy that combines human creative excellence with artificial intelligence precision.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request AI Strategy Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
